import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Account } from '../types';
import { getAccountTypeDisplayName } from '../services/categoryService';
import { Analytics } from '../utils/analytics';

const Accounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'CHECKING',
    balance: '0',
    currency: 'EUR',
    description: '',
    includeInDailyBudget: true
  });
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/accounts');
      setAccounts(response.data.data.accounts);
    } catch (error) {
      console.error('Fehler beim Laden der Konten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/accounts/${editingId}`, formData);
      } else {
        await api.post('/accounts', formData);
        Analytics.accountCreated(formData.type);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', type: 'CHECKING', balance: '0', currency: 'EUR', description: '', includeInDailyBudget: true });
      fetchAccounts();
    } catch (error) {
      console.error('Fehler beim Speichern des Kontos:', error);
    }
  };

  const handleEdit = (account: Account) => {
    setEditingId(account.id);
    setFormData({
      name: account.name,
      type: account.type,
      balance: account.balance.toString(),
      currency: account.currency,
      description: account.description || '',
      includeInDailyBudget: (account as any).includeInDailyBudget !== undefined ? (account as any).includeInDailyBudget : true
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', type: 'CHECKING', balance: '0', currency: 'EUR', description: '', includeInDailyBudget: true });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Konto wirklich löschen?')) {
      try {
        await api.delete(`/accounts/${id}`);
        Analytics.accountDeleted();
        fetchAccounts();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
      }
    }
  };

  if (loading) {
    return <div className="loading">Lädt...</div>;
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        marginBottom: '20px',
        gap: isMobile ? '15px' : '0'
      }}>
        <h1 style={{ fontSize: isMobile ? '24px' : '32px', margin: 0 }}>Konten</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)}
          style={{
            fontSize: isMobile ? '14px' : '16px',
            padding: isMobile ? '10px 20px' : '10px 20px',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          {showForm ? 'Abbrechen' : '+ Neues Konto'}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ padding: isMobile ? '15px' : '20px' }}>
          <h2 style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: isMobile ? '15px' : '20px' }}>
            {editingId ? 'Konto bearbeiten' : 'Neues Konto erstellen'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label style={{ fontSize: isMobile ? '14px' : '16px' }}>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                style={{
                  fontSize: isMobile ? '14px' : '16px',
                  padding: isMobile ? '10px' : '12px'
                }}
              />
            </div>
            <div className="form-group">
              <label style={{ fontSize: isMobile ? '14px' : '16px' }}>Typ</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                style={{
                  fontSize: isMobile ? '14px' : '16px',
                  padding: isMobile ? '10px' : '12px'
                }}
              >
                <option value="CHECKING">Girokonto</option>
                <option value="SAVINGS">Sparkonto</option>
                <option value="CASH">Bargeld</option>
                <option value="CREDIT_CARD">Kreditkarte</option>
                <option value="INVESTMENT">Anlagekonto</option>
                <option value="OTHER">Sonstiges</option>
              </select>
            </div>
            <div className="form-group">
              <label style={{ fontSize: isMobile ? '14px' : '16px' }}>{editingId ? 'Saldo' : 'Anfangssaldo'}</label>
              <input
                type="number"
                step="0.01"
                value={formData.balance}
                onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                required
                style={{
                  fontSize: isMobile ? '14px' : '16px',
                  padding: isMobile ? '10px' : '12px'
                }}
              />
            </div>
            <div className="form-group">
              <label style={{ fontSize: isMobile ? '14px' : '16px' }}>Beschreibung (optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                style={{
                  fontSize: isMobile ? '14px' : '16px',
                  padding: isMobile ? '10px' : '12px',
                  minHeight: isMobile ? '80px' : '100px'
                }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px'
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  backgroundColor: formData.includeInDailyBudget ? '#6f42c1' : '#f0f0f0',
                  border: '2px solid ' + (formData.includeInDailyBudget ? '#6f42c1' : '#ccc'),
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}>
                  {formData.includeInDailyBudget && (
                    <span style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>✓</span>
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={formData.includeInDailyBudget}
                  onChange={(e) => setFormData({ ...formData, includeInDailyBudget: e.target.checked })}
                  style={{ display: 'none' }}
                />
                <span>In Tagesbudget einbeziehen</span>
              </label>
              <small style={{ 
                display: 'block', 
                marginTop: '5px', 
                color: '#666',
                fontSize: isMobile ? '12px' : '13px',
                marginLeft: '34px'
              }}>
                Deaktivieren Sie dies für Sparkonten, Depots oder andere Konten, die nicht für tägliche Ausgaben verfügbar sind
              </small>
            </div>
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '10px' : '10px' 
            }}>
              <button 
                type="submit" 
                className="btn btn-success"
                style={{
                  fontSize: isMobile ? '14px' : '16px',
                  padding: isMobile ? '12px' : '10px 20px',
                  flex: isMobile ? '1' : 'none'
                }}
              >
                {editingId ? 'Änderungen speichern' : 'Konto erstellen'}
              </button>
              {editingId && (
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCancel}
                  style={{
                    fontSize: isMobile ? '14px' : '16px',
                    padding: isMobile ? '12px' : '10px 20px',
                    flex: isMobile ? '1' : 'none'
                  }}
                >
                  Abbrechen
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      <div className="card">
        {accounts.length === 0 ? (
          <p>Keine Konten vorhanden. Erstellen Sie Ihr erstes Konto!</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Typ</th>
                <th>Saldo</th>
                <th>Währung</th>
                <th>Beschreibung</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account: Account) => (
                <tr key={account.id}>
                  <td data-label="Name">{account.name}</td>
                  <td data-label="Typ">{getAccountTypeDisplayName(account.type)}</td>
                  <td data-label="Saldo" style={{ color: parseFloat(account.balance.toString()) >= 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                    {parseFloat(account.balance.toString()).toFixed(2)} {account.currency}
                  </td>
                  <td data-label="Währung">{account.currency}</td>
                  <td data-label="Beschreibung">{account.description || '-'}</td>
                  <td data-label="Aktionen">
                    <div style={{ 
                      display: 'flex', 
                      gap: isMobile ? '5px' : '5px',
                      flexDirection: isMobile ? 'column' : 'row'
                    }}>
                      <button
                        className="btn btn-primary"
                        style={{ 
                          fontSize: isMobile ? '11px' : '12px', 
                          padding: isMobile ? '6px 12px' : '5px 10px',
                          flex: isMobile ? '1' : 'none'
                        }}
                        onClick={() => handleEdit(account)}
                      >
                        Bearbeiten
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ 
                          fontSize: isMobile ? '11px' : '12px', 
                          padding: isMobile ? '6px 12px' : '5px 10px',
                          flex: isMobile ? '1' : 'none'
                        }}
                        onClick={() => handleDelete(account.id)}
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Accounts;
